/**
 * PrepRequestDetail — view of a single preparation request.
 *
 * Opened by clicking a prep notebook card on the shared pipeline. Reuses the
 * shared NotebookPaper styling so it reads as the same instruction sheet the
 * originator filled in: client, appointment, deadline, and the numbered task
 * list. A request is loaded by the Adviser OR the Administration Assistant
 * (§26.1) — never assume the Adviser; read `createdBy`.
 *
 * INBOX DRAW GATE (CLAUDE.md §40): while a request is still in Inbox it is
 * SEALED for the New Business Assistant — she sees the header fields only
 * (status, client, language, appointment, deadline, priority) and a "sealed
 * until started" card in place of the written instructions, voice note,
 * member sections, prior rounds, task list and notepad, until she clicks
 * Start preparation. This mirrors, one for one, FileDetail's "File sealed
 * until drawn" card for the Administration Assistant; the rule lives in the
 * shared isSealedInInbox helper. The Adviser and the originating
 * Administration Assistant are never sealed.
 *
 * ROLE GATING (Phase 5): interactive controls — status transition buttons and
 * task toggles — appear ONLY when the current role is New Business Assistant.
 * For the Adviser and Administration Assistant the sheet carries no status
 * buttons and no task toggles; their one write path is task amendment (§26.7),
 * open to the Adviser on any amendable request and to the Administration
 * Assistant only on requests he originated.
 *
 * The New Business Assistant may toggle a task's completed state (persisted via
 * a read-modify-write of the tasks JSONB) but may NOT edit task text or add /
 * remove tasks — the task list is the originator's instruction set.
 *
 * Status transitions and the timestamps they set/clear:
 *   Inbox       → In Progress  ("Start preparation")  set started_at = now()
 *   In Progress → Completed     ("Mark complete")       set completed_at = now()
 *   Completed   → In Progress  ("Reopen")               clear completed_at
 *   In Progress → Inbox         ("Revert to Inbox")  clear started_at
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, CheckCircle2, Circle, AlertCircle, Loader, Trash2,
  Pencil, BellRing, FolderPlus, RotateCcw,
} from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { getRoleLabel, getFirstName } from '../auth/authConfig';
import { getPrepRequestById, updatePrepRequest, deletePrepRequest } from '../api/prepRequestsApi';
import { formatDateTime, getTodaySASTDateString } from '../utils/dateHelpers';
import { returnToDashboard, movedToMessage } from '../utils/returnToDashboard';
import { previousBusinessDay } from '../utils/businessDays';
import {
  computePrepDeadline,
  hasUnacknowledgedAmendment,
  canAmendPrepTasks,
  canOpenPrepRequest,
  formatPrepYmd,
  formatPrepDeadlineWeekday,
  getPrepUrgency,
  prepTaskProgress,
  STATUS_LABELS,
} from '../utils/prepRequests';
import { NotebookPage, nb, notebookCss } from '../components/prep/NotebookPaper';
import PrepTaskListEditor from '../components/prep/PrepTaskListEditor';
import DeletePrepModal, { styles as deleteModalStyles } from '../components/prep/DeletePrepModal';
import MemberSections from '../components/prep/MemberSections';
import PriorRoundsHistory from '../components/prep/PriorRoundsHistory';
import PrepMetaStrip from '../components/prep/PrepMetaStrip';
import SealedPrepCard from '../components/prep/SealedPrepCard';
import PrepStatusControls, { styles as statusControlStyles } from '../components/prep/PrepStatusControls';
import { newPrepTask, normalisePrepTasks, dropBlankTasks, numberPrepTasks } from '../utils/prepTasks';
import { toggleTaskCompletion } from '../utils/prepTaskCompletion';
import PrepNotes from '../components/prep/PrepNotes';
import { createSystemNote, NOTE_BODY_MAX } from '../api/prepNotesApi';
import { amendmentNoteBodies, clipText } from '../utils/prepAmendmentNotes';
import { drawPrep, completePrep, reopenPrep, revertPrepToInbox } from '../services/prepPipelineService';
import { isSealedInInbox } from '../utils/inboxDrawGate';

function formatDeadlineLong(date) {
  if (!date) return '—';
  return formatPrepDeadlineWeekday(date);
}

export default function PrepRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const isNB = role === 'new_business_assistant';
  const isAdviser = role === 'adviser';

  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  // Adviser-only delete — mirrors the client-file hard delete UX.
  // ---- Adviser task amendment (§26.7) ----------------------------------
  // Amending edits a DRAFT copy; nothing is persisted until Save, so
  // cancelling is a true no-op. Completed tasks are carried through
  // untouched — they are locked for everyone, the Adviser included.
  const [amending, setAmending] = useState(false);
  const [draftTasks, setDraftTasks] = useState([]);
  const [amendError, setAmendError] = useState(null);
  const [savingAmendment, setSavingAmendment] = useState(false);
  // Bumped after an amendment so the notes log below refetches and shows the
  // audit entries without a reload.
  const [notesRefresh, setNotesRefresh] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getPrepRequestById(id);
        if (active) setPrep(data);
      } catch (err) {
        if (active) setError(err.message || 'Could not load this preparation request.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const assistantLabel = getRoleLabel('new_business_assistant');

  // Persist a patch and refresh the open view immediately. On failure, surface
  // the error and (for task toggles) the caller rolls back the optimistic state.
  const applyPatch = async (patch) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await updatePrepRequest(id, patch);
      setPrep(updated);
      return true;
    } catch (err) {
      setError(err.message || 'Could not save the change.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Column-changing transitions go through prepPipelineService (CLAUDE.md
  // §26.11): the service writes the status (and, for the draw, its system
  // note) and returns the updated record; this wrapper applies it and
  // returns the user to the dashboard with the card highlighted in its new
  // column once the whole success chain has resolved (§39). Everything else
  // that calls applyPatch directly (task ticks, amendments, acknowledgement,
  // member ticks) stays on the page. startNewRound is the one named
  // exception: it moves the request to Inbox but deliberately stays here so
  // the Adviser can write the new round's tasks.
  //
  // A failed status write throws out of the service: the error is shown
  // here and nothing else happens. A failed note is swallowed inside the
  // service — it never undoes the move and never strands the page.
  const transition = async (run, nextStatus) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await run();
      setPrep(updated);
      returnToDashboard(navigate, {
        role,
        cardType: 'prep',
        cardId: id,
        message: movedToMessage(STATUS_LABELS[nextStatus] || nextStatus),
      });
      return true;
    } catch (err) {
      setError(err.message || 'Could not save the change.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // The acting user, built as pipelineService's callers build it (FileDetail):
  // the draw's system note names actor.firstName.
  const actor = () => ({ email: user?.email, id: user?.id, firstName: getFirstName(user?.email), role });

  // The prep DRAW (Inbox → In Progress; Inbox draw gate, CLAUDE.md §40): the
  // status write plus the "Preparation started by ⟨name⟩." system note, both
  // inside drawPrep. Neither draw-gate rule applies to prep requests (§40.3).
  const startPreparation = () => transition(() => drawPrep(prep, actor()), 'in_progress');

  const doComplete = () => transition(() => completePrep(prep), 'completed');

  const handleMarkComplete = () => {
    // Completion gate (§26.8): while an amendment is unacknowledged the New
    // Business Assistant may be working from a task list that has changed
    // under her, so completion is blocked until she has read the changes.
    // The button is already disabled; this is the write-time backstop and it
    // explains itself rather than failing silently.
    if (hasUnacknowledgedAmendment(prep)) {
      setError(
        'The task list was amended after this request was submitted. Please read the changes and tick "I have read the changes" before marking it complete.'
      );
      return;
    }
    const { done, total } = prepTaskProgress(prep);
    if (done < total) {
      setConfirmComplete(true); // gentle confirm; don't hard-block
    } else {
      doComplete();
    }
  };

  const reopenToInProgress = () => transition(() => reopenPrep(prep), 'in_progress');

  const revertToSubmitted = () => transition(() => revertPrepToInbox(prep), 'submitted');

  // Task toggle — optimistic, with read-modify-write of the tasks array so
  // other tasks are untouched. The completion cascade (parent ⇄ sub-tasks,
  // §28.4) lives in prepTaskCompletion.js; the ordering-invariant safety
  // net runs on this write as on every save path. Rolls back if the
  // persist fails.
  const toggleTask = async (taskId) => {
    if (!prep) return;
    const prev = prep.tasks;
    const nextTasks = normalisePrepTasks(toggleTaskCompletion(prep.tasks || [], taskId));
    setPrep({ ...prep, tasks: nextTasks }); // optimistic
    const ok = await applyPatch({ tasks: nextTasks });
    if (!ok) setPrep((cur) => ({ ...cur, tasks: prev })); // rollback
  };

  // ---- Amendment handlers (Adviser only, Inbox / In Progress only) -------

  const startAmending = () => {
    // Draft copy of the stored list, normalised: any legacy task missing an
    // id gets one here (ids are the only thing tying a tick to its task),
    // an unknown parent reads as top-level, and the array is put in
    // parent-then-children order before the editor sees it.
    setDraftTasks(normalisePrepTasks(prep.tasks || []));
    setAmendError(null);
    setAmending(true);
  };

  const cancelAmending = () => {
    setAmending(false);
    setDraftTasks([]);
    setAmendError(null);
  };

  const saveAmendment = async () => {
    // Defensive re-verification AT WRITE TIME: role + originator +
    // amendable status, in one call. The controls are already gated the
    // same way in the UI, but UI state is never trusted. Roles are derived
    // in the frontend and never stored in the database (§24.1), so RLS
    // cannot tell an Adviser from either assistant on this table — this
    // check is the enforcement point (§26.1/§26.7).
    if (!canAmendPrepTasks(prep, role, user?.email)) {
      setAmendError(
        role === 'assistant'
          ? 'You can only amend preparation requests you loaded, and only while they are in Inbox or In Progress.'
          : 'Only the Adviser or the request\'s originator can amend tasks, and only while the request is in Inbox or In Progress.'
      );
      return;
    }

    const previous = prep.tasks || [];
    // Completed tasks are locked: carry them through from the STORED record
    // exactly as they are, whatever the draft happens to hold.
    // Blank rows are dropped (a blank parent's sub-tasks are promoted, not
    // lost) and the ordering-invariant safety net runs before the write. A
    // locked task's stored `parent` may point at a task the draft removed
    // or demoted; normalisation resolves that (unknown → top-level, too
    // deep → grandparent) exactly as it does on read.
    const lockedById = new Map(previous.filter((t) => t.completed).map((t) => [t.id, t]));
    const cleaned = normalisePrepTasks(
      dropBlankTasks(draftTasks.map((t) => (lockedById.has(t.id) ? lockedById.get(t.id) : t)))
    );

    if (cleaned.length === 0) {
      setAmendError('A preparation request needs at least one task.');
      return;
    }

    setSavingAmendment(true);
    setAmendError(null);
    try {
      // Only `tasks` is sent: the appointment date, deadline, status and every
      // timing field are untouched by an amendment (§26.7).
      // `tasksAmendedAt` raises the acknowledgement flag for the New
      // Business Assistant (§26.8). Stamping it in the SAME write as the
      // tasks means the flag can never be missed by a partial save.
      const updated = await updatePrepRequest(id, {
        tasks: cleaned,
        tasksAmendedAt: new Date().toISOString(),
      });
      setPrep(updated);

      // Audit trail — one immutable system note per change. Written after the
      // save so a failed save leaves no note behind, and never rewriting
      // history. A note failure is logged but does not undo the amendment.
      const bodies = amendmentNoteBodies(previous, cleaned, getFirstName(user?.email));
      for (const body of bodies) {
        try {
          await createSystemNote(id, body);
        } catch (noteErr) {
          console.warn('[prep-amend] audit note failed:', body, noteErr);
        }
      }
      if (bodies.length > 0) setNotesRefresh((n) => n + 1);

      setAmending(false);
      setDraftTasks([]);
    } catch (err) {
      setAmendError(err.message || 'Could not save the amended tasks.');
    } finally {
      setSavingAmendment(false);
    }
  };

  // ---- Amendment acknowledgement (New Business Assistant, §26.8) --------

  const [acknowledging, setAcknowledging] = useState(false);

  // Acknowledging clears the flag by stamping "read at" later than the
  // amendment. Role is computed HERE, at write time, client-side — the
  // backend cannot infer which role is active (roles are never stored in the
  // database, §24.1), the same reason note acknowledgement routes at write
  // time (§31.2).
  const acknowledgeAmendment = async () => {
    if (role !== 'new_business_assistant') return;
    setAcknowledging(true);
    const ok = await applyPatch({ tasksAmendmentAckAt: new Date().toISOString() });
    setAcknowledging(false);
    return ok;
  };

  // ---- Start new round (Adviser, completed requests only — §26.10) ------

  const [showNewRoundModal, setShowNewRoundModal] = useState(false);
  const [newRoundDate, setNewRoundDate] = useState('');
  const [newRoundError, setNewRoundError] = useState(null);
  const [startingRound, setStartingRound] = useState(false);
  const todaySAST = getTodaySASTDateString();

  const startNewRound = async () => {
    // Defensive re-verification at write time — the button is already
    // Adviser-only and completed-only, but UI state is never trusted (the
    // same client-side enforcement posture as saveAmendment, §26.7).
    if (role !== 'adviser' || prep.status !== 'completed') {
      setNewRoundError('Only the Adviser can start a new round, and only on a completed request.');
      return;
    }
    // New deadline: required, today-or-future — the same validation the
    // submission form applies when a request is first loaded (§26.2).
    if (!newRoundDate) {
      setNewRoundError('Please choose an appointment date for the new round.');
      return;
    }
    if (newRoundDate < todaySAST) {
      setNewRoundError('The appointment date must be today or in the future.');
      return;
    }

    const closedRoundNumber = (prep.priorRounds || []).length + 1;
    // Snapshot the closing round EXACTLY as stored — tasks with their tick
    // state, the round's deadline anchor, its timestamps, and (for group
    // requests) the whole member_sections object with its per-member
    // prepared state. Prior rounds are permanent records (§26.10): nothing
    // ever edits, reorders, deletes or restates them.
    const closedRound = {
      round: closedRoundNumber,
      tasks: prep.tasks || [],
      appointmentDate: prep.appointmentDate,
      startedAt: prep.startedAt,
      completedAt: prep.completedAt,
      memberSections: prep.memberSections || null,
    };
    // Group requests: the new round keeps the member roster and the
    // Adviser's per-member notes, but the "prepared" working ticks reset —
    // a fresh round of preparation starts unprepared (§26.10).
    const nextMemberSections = prep.memberSections
      ? {
          ...prep.memberSections,
          sections: (prep.memberSections.sections || []).map((sec) => ({
            ...sec,
            prepared: false,
          })),
        }
      : null;

    setStartingRound(true);
    setNewRoundError(null);
    try {
      const updated = await updatePrepRequest(id, {
        priorRounds: [...(prep.priorRounds || []), closedRound],
        tasks: [],
        appointmentDate: newRoundDate,
        status: 'submitted',
        startedAt: null,
        completedAt: null,
        // A new round starts with no unacknowledged-amendment flag (§26.8);
        // amendments to the new round's tasks raise it as normal.
        tasksAmendedAt: null,
        tasksAmendmentAckAt: null,
        memberSections: nextMemberSections,
      });
      setPrep(updated);

      // System note — round number, who, when (note timestamp), new deadline.
      // Clipped as a hard guarantee against the 200-char prep-note CHECK.
      const noteBody = clipText(
        `Round ${closedRoundNumber + 1} started by ${getFirstName(user?.email)}. Appointment ${formatPrepYmd(newRoundDate)}.`,
        NOTE_BODY_MAX
      );
      try {
        await createSystemNote(id, noteBody);
        setNotesRefresh((n) => n + 1);
      } catch (noteErr) {
        console.warn('[prep-round] system note failed:', noteErr);
      }

      setShowNewRoundModal(false);
      setNewRoundDate('');

      // Drop straight into the task editor so the new round's instructions
      // can be typed immediately — the same shared PrepTaskListEditor and
      // keyboard conventions as loading a new request (§26.7), with the
      // amendment rules (and their audit notes) applying as normal. The
      // new round starts from one blank line in the normalised item shape.
      setDraftTasks([newPrepTask()]);
      setAmendError(null);
      setAmending(true);
    } catch (err) {
      setNewRoundError(err.message || 'Could not start the new round.');
    } finally {
      setStartingRound(false);
    }
  };

  // Per-member "prepared" tick on segmented review-handoff requests
  // (§34.11) — a working aid only: it gates NOTHING (no effect on prep
  // statuses, stats, completion, or SLA). Same optimistic
  // read-modify-write pattern as task toggles, over the whole
  // member_sections object.
  const togglePrepared = async (reviewClientId) => {
    if (!prep?.memberSections) return;
    const prevSections = prep.memberSections;
    const nextSections = {
      ...prevSections,
      sections: (prevSections.sections || []).map((s) =>
        s.reviewClientId === reviewClientId ? { ...s, prepared: !s.prepared } : s
      ),
    };
    setPrep({ ...prep, memberSections: nextSections }); // optimistic
    const ok = await applyPatch({ memberSections: nextSections });
    if (!ok) setPrep((cur) => ({ ...cur, memberSections: prevSections })); // rollback
  };

  // Adviser-only hard delete — mirrors the client-file delete: remove the row,
  // navigate to the dashboard with the same success-toast state. For test
  // records the affordance renders as "Delete Test Request" (CLAUDE.md §33)
  // and re-verifies is_test + the Adviser role before executing.
  const handleDelete = async () => {
    // Defensive re-verification for the test-record delete path.
    if (prep.isTest && (role !== 'adviser' || prep.isTest !== true)) {
      setShowDeleteConfirm(false);
      return;
    }
    try {
      setDeleting(true);
      await deletePrepRequest(id);
      navigate('/', { state: { deleteSuccess: true, deletedName: prep.clientName } });
    } catch (err) {
      setError(err.message || 'Failed to delete preparation request.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // App-level role gate (CLAUDE.md §30 / §26.1). Preparation requests are
  // openable by the Adviser and the New Business Assistant on any request,
  // and by the Administration Assistant ONLY on requests he loaded himself.
  // The board renders the cards he may not open inert, so this check
  // protects deep links / direct URLs — refused with the friendly message
  // rather than a silent redirect. Keyed off the ACTIVE role (and the
  // signed-in email for the originator test), so switching roles changes
  // access immediately. App-level workflow gating only — RLS is untouched.
  if (loading) {
    return (
      <div style={styles.outer}>
        <style>{notebookCss}</style>
        <div style={styles.loadingRow}><Loader size={18} className="spin" /> Loading…</div>
      </div>
    );
  }

  if (error && !prep) {
    return (
      <div style={styles.outer}>
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error || 'Preparation request not found.'}</span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>
      </div>
    );
  }

  // Amendment is Adviser-only and status-bound (§26.7). `preparation_requests`
  // has exactly three statuses, so this is "not completed" — but it is written
  // as an explicit allow-list so a future status is non-amendable by default.
  // Who may amend (CLAUDE.md §26.1/§26.7): the Adviser any amendable request,
  // the Administration Assistant only ones HE originated. The New Business
  // Assistant never amends — she works the request.
  const canAmendTasks = canAmendPrepTasks(prep, role, user?.email);
  // Rounds (§26.10): the current round number is one past the closed
  // history; the ordinary task list below IS the current round.
  const priorRounds = Array.isArray(prep.priorRounds) ? prep.priorRounds : [];
  const currentRoundNumber = priorRounds.length + 1;
  // Unacknowledged amendment (§26.8): the banner and the completion gate both
  // read this one derived value, so they can never disagree.
  const amendmentPending = hasUnacknowledgedAmendment(prep);

  // App-level role gate (CLAUDE.md §30 + §26.1). The Administration
  // Assistant may open ONLY preparation requests he originated — he loads
  // them himself now, and amends his own — so the refusal is scoped to
  // requests loaded by someone else rather than to the role as a whole.
  // Evaluated here (not before the fetch) because it depends on the record.
  if (!canOpenPrepRequest(prep, role, user?.email)) {
    return (
      <div style={styles.outer}>
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>
            You can only open preparation requests you loaded. This one was loaded by
            someone else.
          </span>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back to dashboard
        </button>
      </div>
    );
  }

  const deadline = computePrepDeadline(prep);
  const urgency = getPrepUrgency(prep);
  const deadlineTight = urgency === 'overdue' || urgency === 'soon';
  const tasks = Array.isArray(prep.tasks) ? prep.tasks : [];
  const { done, total } = prepTaskProgress(prep);
  // Inbox draw gate (§40): sealed for the New Business Assistant while the
  // request is still in Inbox. Same helper as FileDetail, the board and the
  // login catch-up gate. Never true for the Adviser or the originating
  // Administration Assistant; test records are sealed like real ones.
  const isSealedForNB = isSealedInInbox('prep', prep.status, role);

  return (
    <div style={styles.outer}>
      <style>{notebookCss}</style>

      <div style={styles.topBar}>
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={styles.eyebrow}>Preparation request</div>
      </div>

      {/* Metadata strip */}
      <PrepMetaStrip prep={prep} user={user} isNB={isNB} done={done} total={total} />

      {/* Non-blocking error (e.g. a failed save) */}
      {error && (
        <div style={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Amendment banner (§26.8) — New Business Assistant only. The task
          list changed after she last looked at it, so say so plainly and
          hold completion until she has read it. Uses the existing banner
          shape and tokens; dismissing it IS the acknowledgement. */}
      {/* Hidden while sealed (§40, decision D2): it re-raises unchanged the
          moment the request is started; the completion gate it protects
          only applies once the request is In Progress anyway. */}
      {isNB && amendmentPending && !isSealedForNB && (
        <motion.div
          style={styles.amendBanner}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <BellRing size={16} />
          <span style={{ flex: 1 }}>
            The task list was amended on {formatDateTime(prep.tasksAmendedAt)}. Please read
            the changes below — you can mark this request complete once you have.
          </span>
          <button
            className="btn btn-primary"
            onClick={acknowledgeAmendment}
            disabled={acknowledging || busy}
          >
            {acknowledging
              ? <><Loader size={16} className="spin" /> Saving…</>
              : 'I have read the changes'}
          </button>
        </motion.div>
      )}

      <NotebookPage>
        <div style={nb.fieldRow}>
          <span style={nb.label}>Client:</span>
          <span style={nb.readValue}>{prep.clientName}</span>
        </div>
        <div style={nb.fieldRow}>
          <span style={nb.label}>Language:</span>
          <span style={nb.readValue}>{prep.clientLanguagePreference || 'English'}</span>
        </div>
        <div style={nb.fieldRow}>
          <span style={nb.label}>Appointment:</span>
          <span style={nb.readValue}>{formatPrepYmd(prep.appointmentDate)}</span>
          {/* Test records have no deadline — the calculation and all
              overdue highlighting are skipped (CLAUDE.md §33). */}
          {!prep.isTest && (
            <span style={{ ...nb.deadline, ...(deadlineTight ? nb.deadlineTight : {}) }}>
              → Deadline: {formatDeadlineLong(deadline)}
              {urgency === 'overdue' ? ' — OVERDUE' : ''}
            </span>
          )}
        </div>
        <div style={nb.fieldRow}>
          <span style={nb.label}>Priority:</span>
          <span style={{ ...nb.readValue, color: prep.priority === 'urgent' ? 'var(--cp-danger)' : '#1f3a5c' }}>
            {prep.priority === 'urgent' ? 'Urgent' : 'Normal'}
          </span>
        </div>
        {isSealedForNB ? (
          /* Inbox draw gate (CLAUDE.md §40) — the prep counterpart of
             FileDetail's "File sealed until drawn" card. Everything beneath
             the header fields (written instructions, voice note, member
             sections and their prepared ticks, prior rounds, the task list
             and its ticks, the amend control) is withheld until the New
             Business Assistant clicks Start preparation. Tokens only. */
          <SealedPrepCard />
        ) : (
        /* Unsealed sheet — unchanged content; the wrapper fragment is the
           only addition, so the block is deliberately not re-indented. */
        <>
        {prep.writtenInstructions && (
          <div style={{ ...nb.fieldRow, display: 'block' }}>
            <span style={{ ...nb.label, display: 'block', marginBottom: '4px' }}>
              Written Instructions / Additional Notes
            </span>
            <span style={{ ...nb.summaryText, whiteSpace: 'pre-wrap' }}>
              {prep.writtenInstructions}
            </span>
          </div>
        )}

        {/* The Adviser's voice note (§26.6) — played back exactly as a client
            file's voice note is. Attached as-is: nothing is transcribed or
            drafted from it (the task lines are typed by the adviser). Group
            review-handoff requests keep their separate meeting note below
            (§34.11). */}
        {prep.voiceNoteUrl && (
          <div style={{ ...nb.fieldRow, display: 'block' }}>
            <span style={{ ...nb.label, display: 'block', marginBottom: '4px' }}>
              Voice note
            </span>
            <audio controls src={prep.voiceNoteUrl} style={{ width: '100%', maxWidth: '420px' }} />
          </div>
        )}

        {/* Segmented member sections (§34.11) — review-handoff GROUP
            requests only; ordinary requests (member_sections null)
            render exactly as before. The per-member "prepared" tick is
            a working aid ONLY: it gates nothing — statuses, stats,
            completion and SLA are untouched by it. */}
        {prep.memberSections && (
          <MemberSections prep={prep} isNB={isNB} busy={busy} togglePrepared={togglePrepared} />
        )}

        <PriorRoundsHistory priorRounds={priorRounds} />

        <div style={nb.divider}>
          — {priorRounds.length > 0 ? `Round ${currentRoundNumber} · ` : ''}Tasks for {assistantLabel} —
        </div>

        {/* Task amendment (§26.7). The control is rendered ONLY for the
            Adviser (any request) or the Administration Assistant on a
            request HE loaded (§26.1), and ONLY while the request is in
            Inbox or In Progress — the New Business Assistant never sees
            it, and a completed request's task list is frozen for
            everyone. */}
        {canAmendTasks && !amending && (
          <button type="button" className="np-add-task" onClick={startAmending}>
            <Pencil size={13} style={{ verticalAlign: '-2px', marginRight: '5px' }} />
            amend tasks
          </button>
        )}

        {amending ? (
          <>
            {/* Shared two-level editor (tasks + sub-tasks). Completed tasks
                are locked in it; the save path carries their STORED record
                through whatever the draft holds. */}
            <PrepTaskListEditor
              tasks={draftTasks}
              onChange={setDraftTasks}
              lockedIds={new Set(draftTasks.filter((t) => t.completed).map((t) => t.id))}
              disabled={savingAmendment}
            />

            {amendError && (
              <div style={{ ...styles.errorBanner, marginTop: '10px', marginBottom: 0 }}>
                <AlertCircle size={16} />
                <span>{amendError}</span>
              </div>
            )}

            <div style={{ ...styles.actions, marginTop: '14px' }}>
              <button
                className="btn btn-secondary"
                onClick={cancelAmending}
                disabled={savingAmendment}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveAmendment}
                disabled={savingAmendment}
              >
                {savingAmendment
                  ? <><Loader size={16} className="spin" /> Saving…</>
                  : 'Save amended tasks'}
              </button>
            </div>
          </>
        ) : (
        <div style={nb.tasks}>
          {tasks.length === 0 && (
            <div style={{ ...nb.summaryText, color: '#8a7a55' }}>No tasks recorded.</div>
          )}
          {numberPrepTasks(tasks).map(({ task: t, label, depth }, i) => {
            const TickIcon = t.completed ? CheckCircle2 : Circle;
            const tickColour = t.completed ? '#2EC4B6' : '#a9b6c6';
            return (
              <div key={t.id || i} style={{ ...nb.taskRow, paddingLeft: depth === 1 ? '36px' : 0 }}>
                <span style={{ ...nb.taskNum, minWidth: depth === 1 ? '40px' : '24px' }}>{label}</span>
                {isNB ? (
                  /* Each sub-task has its own tick; ticking a parent ticks
                     all its sub-tasks and vice versa (prepTaskCompletion). */
                  <button
                    type="button"
                    onClick={() => toggleTask(t.id)}
                    disabled={busy}
                    aria-pressed={!!t.completed}
                    aria-label={t.completed ? `Mark task ${label} not done` : `Mark task ${label} done`}
                    style={styles.tickButton}
                  >
                    <TickIcon size={18} color={tickColour} />
                  </button>
                ) : (
                  <TickIcon size={17} color={tickColour} style={{ flex: '0 0 auto', alignSelf: 'center' }} />
                )}
                <span
                  style={{
                    ...nb.summaryText,
                    textDecoration: t.completed ? 'line-through' : 'none',
                    color: t.completed ? 'var(--cp-text-muted)' : nb.summaryText.color,
                    // pre-wrap so a task the Adviser wrote across several
                    // lines (Shift+Enter on the form) reads back the same way
                    // here rather than running together on one line.
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t.text}
                </span>
              </div>
            );
          })}
        </div>
        )}
        </>
        )}
      </NotebookPage>

      {/* Notes — append-only progress log between the Adviser and the New
          Business Assistant (CLAUDE.md §18 pattern + §31 acknowledgement).
          Distinct from the Adviser's one-time Written Instructions above.
          Only reachable by the Adviser and NB Assistant (the Administration
          Assistant is redirected out above). */}
      {/* Sealed while the request is in Inbox for the New Business
          Assistant (§40) — mirrors File Notes sitting inside FileDetail's
          sealed branch. */}
      {!isSealedForNB && (
        <PrepNotes
          prepId={prep.id}
          refreshSignal={notesRefresh}
          currentUser={{ id: user?.id || null, displayName: getFirstName(user?.email) }}
          currentRole={role}
        />
      )}

      {/* Status controls — New Business Assistant only */}
      {isNB && (
        <PrepStatusControls prep={prep} busy={busy} amendmentPending={amendmentPending} startPreparation={startPreparation} revertToSubmitted={revertToSubmitted} handleMarkComplete={handleMarkComplete} reopenToInProgress={reopenToInProgress} />
      )}

      {/* Post-completion actions (§26.9) — Adviser only, COMPLETED requests
          only. "Create file folder" opens the NORMAL new-file submission
          form pre-filled from this request (client name, language,
          priority) for review — it never creates the file in one click,
          and cancelling the form has no effect. The request itself stays
          completed, in its stack, untouched by this action. */}
      {isAdviser && prep.status === 'completed' && (
        <div style={styles.actions}>
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate(
                `/submit?clientName=${encodeURIComponent(prep.clientName)}` +
                `&clientLanguage=${encodeURIComponent(prep.clientLanguagePreference || 'English')}` +
                `&priority=${prep.priority === 'urgent' ? 'Urgent' : 'Normal'}` +
                `&fromPrep=${prep.id}`
              )
            }
          >
            <FolderPlus size={16} /> Create File Folder
          </button>
          {/* "Start New Round" (§26.10) reopens the request for another
              round of preparation. Independent of and non-exclusive with
              Create File Folder — either can follow the other. */}
          <button
            className="btn btn-secondary"
            onClick={() => {
              setNewRoundError(null);
              setNewRoundDate('');
              setShowNewRoundModal(true);
            }}
            disabled={startingRound}
          >
            <RotateCcw size={16} /> Start New Round
          </button>
        </div>
      )}

      {/* New-round modal (§26.10): the Adviser must set the new round's
          appointment date before the reopen takes effect — required and
          today-or-future, the same validation the submission form applies
          when a request is first loaded. Cancel is a true no-op. */}
      {showNewRoundModal && (
        <div style={styles.modalOverlay} onClick={() => !startingRound && setShowNewRoundModal(false)}>
          <motion.div
            style={styles.modalCard}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>Start round {currentRoundNumber + 1}?</h3>
            <p style={styles.modalBody}>
              The request returns to Inbox and flows through the pipeline
              again. Round {currentRoundNumber}&apos;s tasks stay on the sheet, permanently
              locked; a fresh task section opens for the new round (add its
              tasks via “amend tasks” once reopened).
            </p>
            <label
              htmlFor="new-round-date"
              style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: 'var(--cp-text)', marginBottom: '0.35rem' }}
            >
              Appointment date for the new round *
            </label>
            <input
              id="new-round-date"
              type="date"
              value={newRoundDate}
              min={todaySAST}
              onChange={(e) => setNewRoundDate(e.target.value)}
              disabled={startingRound}
              style={{ marginBottom: '0.5rem' }}
            />
            {/* Derived deadline preview — same rule as the submission form
                (appointment minus one SA business day). Test records carry
                no deadline (§33), so no preview is shown for them. */}
            {newRoundDate && !prep.isTest && (
              <p style={{ ...styles.modalBody, marginBottom: '0.75rem' }}>
                Due: {formatDeadlineLong(previousBusinessDay(newRoundDate))}
              </p>
            )}
            {newRoundError && (
              <div style={{ ...styles.errorBanner, marginBottom: '0.75rem' }}>
                <AlertCircle size={16} />
                <span>{newRoundError}</span>
              </div>
            )}
            <div style={styles.modalActions}>
              <button
                className="btn btn-outline"
                onClick={() => setShowNewRoundModal(false)}
                disabled={startingRound}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={startNewRound}
                disabled={startingRound}
              >
                {startingRound
                  ? <><Loader size={16} className="spin" /> Starting…</>
                  : `Start round ${currentRoundNumber + 1}`}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete — Adviser only (mirrors the client-file delete affordance) */}
      {isAdviser && (
        <div style={styles.adviserActions}>
          <button
            style={styles.deleteBtn}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
            type="button"
          >
            <Trash2 size={16} /> {prep.isTest ? 'Delete Test Request' : 'Delete Preparation Request'}
          </button>
        </div>
      )}

      {/* Delete confirmation — mirrors the client-file delete modal */}
      {showDeleteConfirm && (
        <DeletePrepModal prep={prep} deleting={deleting} setShowDeleteConfirm={setShowDeleteConfirm} handleDelete={handleDelete} />
      )}

      {/* Gentle confirm: completing with unticked tasks */}
      {confirmComplete && (
        <div style={styles.modalOverlay} onClick={() => setConfirmComplete(false)}>
          <motion.div
            style={styles.modalCard}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={styles.modalTitle}>Mark preparation complete?</h3>
            <p style={styles.modalBody}>
              {total - done} task{total - done === 1 ? ' is' : 's are'} still unticked.
              Mark this preparation complete anyway?
            </p>
            <div style={styles.modalActions}>
              <button className="btn btn-secondary" onClick={() => setConfirmComplete(false)} disabled={busy}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={async () => { const ok = await doComplete(); if (ok) setConfirmComplete(false); }}
                disabled={busy}
              >
                {busy ? <><Loader size={16} className="spin" /> Saving…</> : 'Complete anyway'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

const styles = {
  outer: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '0.5rem 0 2rem',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: 'var(--cp-text-muted)',
    padding: '2rem 0',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
  },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: 'var(--cp-text-muted)',
  },
  // Amendment banner (§26.8) — same shape as the error banner above, in the
  // amber the app already uses for "needs your acknowledgement" (§31.4).
  amendBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6rem',
    background: 'rgba(245, 158, 11, 0.12)',
    border: '1px solid var(--cp-ack-amber)',
    color: 'var(--cp-text)',
    borderRadius: '10px',
    padding: '0.7rem 0.9rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  // Sealed card (Inbox draw gate, §40) — the notebook-side twin of
  // FileDetail's lockedCard, expressed with --cp-* tokens via color-mix.
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(231, 111, 81, 0.12)',
    border: '1px solid var(--cp-warning)',
    color: 'var(--cp-danger)',
    borderRadius: '10px',
    padding: '0.6rem 0.9rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  tickButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    alignSelf: 'center',
    width: '26px',
    height: '26px',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
  },
  actions: statusControlStyles.actions,
  adviserActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1.25rem',
  },
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.55rem 0.9rem',
    background: 'none',
    border: 'none',
    color: 'var(--cp-danger)',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '8px',
  },
  modalOverlay: deleteModalStyles.modalOverlay,
  modalCard: deleteModalStyles.modalCard,
  modalTitle: deleteModalStyles.modalTitle,
  modalBody: deleteModalStyles.modalBody,
  modalActions: deleteModalStyles.modalActions,
};
